import type { ComponentType } from 'react'
import { LogoLoader } from './LogoLoader'

/**
 * "Whatever this app uses while it waits."
 *
 * The auth, entitlement and permission gates all render a loader, and they are
 * SHARED — one copy in @meno/ui serving every app. So a per-app loading screen
 * cannot be chosen at the call site: it has to be something the app declares
 * once about itself, the same way `setCatalogHost()` already declares which
 * host the catalog pages are running inside.
 *
 * Unset, this is the WeTook `LogoLoader` — which is what Frame, My and Ping
 * want and already had, so none of them change. Cashier registers the
 * fingerprint scan in its App.tsx and gets it everywhere it waits, including
 * inside these shared gates, rather than only on the one screen that imported
 * it directly.
 */

type LoaderComponent = ComponentType<{ className?: string; caption?: string }>

let registered: LoaderComponent | null = null

/** Call once, at app module scope, before anything renders. */
export function setAppLoader(loader: LoaderComponent): void {
  registered = loader
}

export function AppLoader({ className, caption }: { className?: string; caption?: string }) {
  const Loader = registered ?? LogoLoader
  return <Loader className={className} caption={caption} />
}

/**
 * The full-screen waiting state — the same one the auth / entitlement gates
 * show. Pages loading their own data use it too, so sign-in → page code → page
 * data reads as ONE continuous loader instead of the gate's logo followed by a
 * page's own spinner in a box.
 */
export function AppLoadingScreen({ caption }: { caption?: string }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-background">
      <AppLoader caption={caption} />
    </div>
  )
}
